import React, { useEffect, useState } from 'react';
import { RefreshCw, Volume2 } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { VoiceCharacteristicBellCurve } from '../BellCurve';
import { createClientLogger } from '~/utils/logger.client';
import { IS_VIDEO_DISABLED } from './utils';

// Lazy logger creation to avoid SSR issues with mobile optimization
let logger: ReturnType<typeof createClientLogger> | null = null;
const getLogger = () => {
  if (!logger && typeof window !== 'undefined') {
    try {
      logger = createClientLogger('AudioSetting');
    } catch (error) {
      // Fallback for mobile devices with memory constraints
      console.warn('Failed to create logger, using console fallback:', error);
      return {
        info: (msg: string, data?: any) => console.info(`[AudioSetting] ${msg}`, data?.metadata || ''),
        warn: (msg: string, data?: any) => console.warn(`[AudioSetting] ${msg}`, data?.metadata || ''),
        error: (msg: string, data?: any) => console.error(`[AudioSetting] ${msg}`, data?.metadata || ''),
        debug: (msg: string, data?: any) => console.debug(`[AudioSetting] ${msg}`, data?.metadata || ''),
      };
    }
  }
  return logger || {
    info: (msg: string, data?: any) => console.info(`[AudioSetting] ${msg}`, data?.metadata || ''),
    warn: (msg: string, data?: any) => console.warn(`[AudioSetting] ${msg}`, data?.metadata || ''),
    error: (msg: string, data?: any) => console.error(`[AudioSetting] ${msg}`, data?.metadata || ''),
    debug: (msg: string, data?: any) => console.debug(`[AudioSetting] ${msg}`, data?.metadata || ''),
  };
};

interface AudioSettingProps {
  analysisData: any;
  primaryFileId?: string;
}

export function AudioSetting({ analysisData, primaryFileId }: AudioSettingProps) {
  const [audioAnalysis, setAudioAnalysis] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  // Circuit breaker state  
  const [failureCount, setFailureCount] = useState(0);
  const maxFailures = 5; // Increased limit to allow more attempts after fixes

  // Check if audio analysis already exists in the database using dedicated fields
  const existingAudioAnalysis = analysisData?.audioAnalyzedAt ? {
    pitchMean: analysisData.audioPitchMean,
    pitchVariation: analysisData.audioPitchVariation,
    volumeLevel: analysisData.audioVolumeLevel,
    toneQuality: analysisData.audioToneQuality,
    speakingRate: analysisData.audioSpeakingRate,
    energyLevel: analysisData.audioEnergyLevel,
    fullAnalysis: analysisData.audioAnalysis,
    analyzedAt: analysisData.audioAnalyzedAt,
  } : null;

  getLogger().info('AudioSetting render state', {
    metadata: {
      hasAnalyzed,
      hasExistingAudioAnalysis: !!existingAudioAnalysis,
      hasAudioAnalysisState: !!audioAnalysis,
      audioAnalysisKeys: audioAnalysis ? Object.keys(audioAnalysis) : null,
      isLoading,
      error: error || null
    }
  });
  getLogger().info('AudioSetting analysis data', {
    metadata: {
      filesCount: analysisData?.files?.length || 0,
      status: analysisData?.status
    }
  });

  // Determine upload status
  const getUploadStatus = () => {
    if (!analysisData?.files) return { status: 'unknown', hasVideo: false, isAudioOnly: false };
    
    const videoFiles = analysisData.files.filter((f: any) => 
      f.key && !f.key.includes('-audio-encrypted') && 
      (f.key.includes('.mp4') || f.key.includes('.webm') || f.key.includes('.mov') || f.type?.startsWith('video/'))
    );
    
    const audioFiles = analysisData.files.filter((f: any) => 
      f.key && (f.key.includes('-audio-encrypted') || f.type?.startsWith('audio/'))
    );

    const hasVideo = videoFiles.length > 0;
    const hasAudio = audioFiles.length > 0;
    const overallStatus = analysisData.status;

    // If we have audio but no video, check if video might still be uploading
    if (hasAudio && !hasVideo) {
      // If analysis is still pending/processing, video might be uploading
      if (overallStatus === 'pending' || overallStatus === 'processing') {
        return { status: 'uploading', hasVideo: false, isAudioOnly: false };
      } else {
        // Analysis is complete but no video - this is audio-only
        return { status: 'audio-only', hasVideo: false, isAudioOnly: true };
      }
    }

    return { 
      status: hasVideo ? 'completed' : 'unknown', 
      hasVideo, 
      isAudioOnly: false 
    };
  };

  const uploadStatus = getUploadStatus();
  getLogger().info('AudioSetting upload status check', {
    metadata: {
      uploadStatus,
      filesCount: analysisData?.files?.length || 0,
      analysisStatus: analysisData?.status
    }
  });

  const analyzeAudio = async (retryCount = 0) => {
    if (!analysisData?.files?.length) {
      setError('No files available for audio analysis');
      return;
    }

    // Check if analysis is already completed by querying the API first
    try {
      const statusResponse = await fetch(`/api/analyze-audio?analysisId=${analysisData?.id}`);
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        if (statusData.status === 'completed' && statusData.results) {
          getLogger().info('AudioSetting analysis already completed, using existing results');
          setAudioAnalysis(statusData.results);
          setHasAnalyzed(true);
          return;
        }
      }
    } catch (err) {
      getLogger().info('AudioSetting status check failed, proceeding with new analysis');
    }

    setIsLoading(true);
    setError('');
    setHasAnalyzed(false);
    
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds
    
    try {
      getLogger().info('AudioSetting starting audio analysis with existing audio file', {
        metadata: { retryCount, maxRetries }
      });
      
      const response = await fetch('/api/analyze-audio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysisId: analysisData?.id,
        }),
      });

      getLogger().info('AudioSetting audio analysis API response', {
        metadata: {
          status: response.status,
          ok: response.ok,
          statusText: response.statusText,
          retryCount
        }
      });

      // Check for 502 Bad Gateway or 503 Service Unavailable - retry
      if ((response.status === 502 || response.status === 503) && retryCount < maxRetries) {
        getLogger().warn('AudioSetting received 502/503, retrying...', {
          metadata: { retryCount, maxRetries, retryDelay }
        });
        setError(`Service temporarily unavailable. Retrying (${retryCount + 1}/${maxRetries})...`);
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return analyzeAudio(retryCount + 1);
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('AudioSetting: Audio analysis API error:', errorText);
        
        // Check if error is HTML (502/503 page)
        if (errorText.includes('<!DOCTYPE html>') || errorText.includes('502') || errorText.includes('503')) {
          throw new Error(`Audio service is temporarily unavailable. Please try again in a moment.`);
        }
        
        throw new Error(`Failed to start audio analysis: ${response.status}`);
      }

      const data = await response.json();
      getLogger().info('AudioSetting audio analysis API response data received', {
        metadata: { hasData: !!data, analysisId: data?.analysisId }
      });
      
      // Poll for results
      await pollForAudioResults(data.analysisId);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze audio. Please try again.';
      setError(errorMessage);
      setHasAnalyzed(true);
      setFailureCount(prev => prev + 1);
      console.error('Audio analysis error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const pollForAudioResults = async (analysisId: string) => {
    const maxAttempts = 30; // 5 minutes max
    let attempts = 0;

    const poll = async () => {
      try {
        getLogger().info('AudioSetting polling for results', {
          metadata: { attempt: attempts + 1, maxAttempts, analysisId }
        });
        
        const response = await fetch(`/api/analyze-audio?analysisId=${analysisId}`);
        getLogger().info('AudioSetting poll response', {
          metadata: {
            status: response.status,
            ok: response.ok,
            statusText: response.statusText
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('AudioSetting: Poll error response:', errorText);
          throw new Error(`Polling failed: ${response.status} ${errorText}`);
        }
        
        const data = await response.json();
        getLogger().info('AudioSetting poll response data', {
          metadata: { status: data?.status, hasResults: !!data?.results }
        });
        
        if (data.status === 'completed' && data.results) {
          getLogger().info('AudioSetting analysis completed successfully', {
            metadata: { hasResults: !!data.results }
          });
          setAudioAnalysis(data.results);
          setHasAnalyzed(true);
          
          // Save results to database
          try {
            const saveResponse = await fetch('/api/save-audio-analysis', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                analysisId: analysisData?.id,
                results: data.results,
              }),
            });
            
            if (saveResponse.ok) {
              getLogger().info('AudioSetting results saved to database successfully');
              // Reload page to refresh analysis data for sidebar and other components
              setTimeout(() => {
                window.location.reload();
              }, 1000); // Small delay to ensure database save is complete
            } else {
              console.warn('AudioSetting: Failed to save results to database:', saveResponse.status);
            }
          } catch (saveError) {
            console.warn('AudioSetting: Error saving results to database:', saveError);
          }
          
          return;
        } else if (data.status === 'failed') {
          console.error('AudioSetting: Analysis failed according to poll response');
          setFailureCount(prev => prev + 1);
          throw new Error('Audio analysis failed');
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          getLogger().info('AudioSetting analysis still processing, retrying', {
            metadata: { attempt: attempts, maxAttempts, retryInSeconds: 10 }
          });
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          console.error('AudioSetting: Analysis timed out after', maxAttempts, 'attempts');
          throw new Error('Audio analysis timed out');
        }
      } catch (err) {
        console.error('AudioSetting: Polling error:', err);
        setError('Audio analysis failed or timed out');
        setHasAnalyzed(true);
        setFailureCount(prev => prev + 1);
      }
    };

    poll();
  };

  // Load existing analysis or trigger new analysis
  useEffect(() => {
    // Check for separate audio file (from recording flow)
    const hasAudioFile = analysisData?.files?.some((f: any) => f.key?.includes('-audio-encrypted'));
    
    // Check for video file (from upload flow or recording flow) - audio can be extracted from video
    const hasVideoFile = IS_VIDEO_DISABLED ? false : analysisData?.files?.some((f: any) => 
      !f.key?.includes('-audio-encrypted') && 
      (f.type?.startsWith('video/') || f.key?.includes('.mp4') || f.key?.includes('.webm') || f.key?.includes('.mov'))
    );
    
    getLogger().info('AudioSetting useEffect conditions', {
      metadata: {
        existingAudioAnalysis: !!existingAudioAnalysis,
        hasAnalyzed,
        isLoading,
        error: !!error,
        hasVideoFile,
        hasAudioFile,
        audioAnalyzedAt: analysisData?.audioAnalyzedAt,
        analysisId: analysisData?.id,
        failureCount,
        maxFailures,
        shouldTriggerWithVideo: !existingAudioAnalysis && !hasAnalyzed && !isLoading && !error && hasVideoFile && failureCount < maxFailures,
        shouldTriggerWithAudio: !existingAudioAnalysis && !hasAnalyzed && !isLoading && !error && hasAudioFile && failureCount < maxFailures,
        filesDebug: analysisData?.files?.map((f: any) => ({ 
          id: f.id, 
          key: f.key, 
          type: f.type,
          isAudioEncrypted: f.key?.includes('-audio-encrypted'),
          isVideoFile: !f.key?.includes('-audio-encrypted') && 
            (f.type?.startsWith('video/') || f.key?.includes('.mp4') || f.key?.includes('.webm') || f.key?.includes('.mov'))
        }))
      }
    });
    
    if (existingAudioAnalysis) {
      // Load existing analysis from database
      getLogger().info('AudioSetting loading existing analysis from database', {
        metadata: { hasExistingAnalysis: !!existingAudioAnalysis }
      });
      setAudioAnalysis(existingAudioAnalysis);
      setHasAnalyzed(true);
      getLogger().info('AudioSetting set audioAnalysis state', {
        metadata: { hasData: !!existingAudioAnalysis }
      });
      return; // Early return to prevent further execution
    } 
    
    // Audio can be analyzed from either:
    // 1. Separate audio file (from recording flow)
    // 2. Video file (from upload flow or recording flow - audio extracted server-side)
    const canAnalyze = hasVideoFile || hasAudioFile;
    
    if (!existingAudioAnalysis && !hasAnalyzed && !isLoading && !error && canAnalyze && failureCount < maxFailures) {
      getLogger().info('AudioSetting triggering analyzeAudio', {
        metadata: { canAnalyze, hasVideoFile, hasAudioFile }
      });
      analyzeAudio();
      return; // Early return to prevent further execution
    } 
    
    if (failureCount >= maxFailures) {
      getLogger().warn('AudioSetting circuit breaker activated - too many failures', {
        metadata: { failureCount, maxFailures }
      });
      setError('Audio analysis failed multiple times. Please refresh the page to try again.');
      setHasAnalyzed(true);
    }
    
    if (!canAnalyze) {
      getLogger().warn('AudioSetting cannot analyze - no suitable files found', {
        metadata: { hasVideoFile, hasAudioFile }
      });
    }
  }, [analysisData?.audioAnalyzedAt, analysisData?.id, analysisData?.files]); // Added files dependency

  // Reset circuit breaker function
  const resetCircuitBreaker = () => {
    setFailureCount(0);
    setError('');
    setHasAnalyzed(false);
    getLogger().info('AudioSetting circuit breaker reset');
  };

  // Force new analysis with fresh state
  const forceNewAnalysis = async () => {
    setFailureCount(0);
    setError('');
    setHasAnalyzed(false);
    setIsLoading(false);
    setAudioAnalysis(null);
    getLogger().info('AudioSetting forcing new analysis');
    
    // Wait a moment for state to settle
    setTimeout(() => {
      analyzeAudio();
    }, 100);
  };

  // Show uploading state when video is still being uploaded
  if (uploadStatus.status === 'uploading') {
    return (
      <Card id="audio-setting" className="border-2 border-brand-dark-blue/30">
        <CardHeader className="bg-gradient-to-r from-brand-dark-blue/10 to-brand-gold/5 mb-4">
          <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
            <Volume2 className="h-6 w-6 text-brand-dark-blue" />
            <Trans>Audio Analysis</Trans>
          </CardTitle>
          <CardDescription className="text-brand-dark-blue/70 font-medium">
            <Trans>AI analysis of your voice pitch, tone, volume, and speaking characteristics</Trans>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <RefreshCw className="h-6 w-6 animate-spin text-brand-gold" />
              <span className="text-lg font-medium text-brand-dark-blue"><Trans>Video Uploading...</Trans></span>
            </div>
            <p className="text-brand-dark-blue/70 text-sm">
              <Trans>Your video is still being uploaded. Audio analysis will begin once the upload is complete.</Trans>
            </p>
            <p className="text-brand-dark-blue/50 text-xs mt-2">
              <Trans>Audio analysis requires the video file to extract high-quality audio data.</Trans>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Only show "not available" message if we have no video file AND no existing audio analysis
  // const hasVideoFile = IS_VIDEO_DISABLED ? false : analysisData?.files?.some((f: any) => !f.key?.includes('-audio-encrypted'));
  // if (!hasVideoFile && uploadStatus.isAudioOnly && !existingAudioAnalysis) {
  //   getLogger().info('AudioSetting showing audio-only not available message', {
  //     metadata: { reason: 'no video file and no existing analysis' }
  //   });
  //   return (
  //     <Card id="audio-setting" className="border-2 border-brand-dark-blue/30">
  //       <CardHeader className="bg-gradient-to-r from-brand-dark-blue/10 to-brand-gold/5 mb-4">
  //         <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
  //           <Volume2 className="h-6 w-6 text-brand-dark-blue" />
  //           Audio Analysis
  //         </CardTitle>
  //         <CardDescription className="text-brand-dark-blue/70 font-medium">
  //           AI analysis of your voice pitch, tone, volume, and speaking characteristics
  //         </CardDescription>
  //       </CardHeader>
  //       <CardContent>
  //         <div className="text-center py-8">
  //           <Volume2 className="h-12 w-12 mx-auto text-brand-dark-blue/30 mb-4" />
  //           <p className="text-brand-dark-blue/70 text-sm">
  //             Audio analysis is not available for audio-only recordings.
  //           </p>
  //           <p className="text-brand-dark-blue/50 text-xs mt-2">
  //             Record with video to get detailed voice analysis including pitch, tone, and speaking characteristics.
  //           </p>
  //         </div>
  //       </CardContent>
  //     </Card>
  //   );
  // }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Loading State */}
      {isLoading && (
        <Card className="border-2 border-brand-dark-blue/30">
          <CardContent>
            <div className="text-center py-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <RefreshCw className="h-6 w-6 animate-spin text-brand-dark-blue" />
                <span className="text-lg font-medium text-brand-dark-blue"><Trans>Analyzing Audio...</Trans></span>
              </div>
              <p className="text-sm text-brand-dark-blue/70">
                <Trans>This may take a few minutes as we analyze your voice characteristics</Trans>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-2 border-brand-dark-blue/30">
          <CardContent>
            <div className="text-center py-4">
              <p className="text-brand-error text-sm mb-2">{error || 'Analysis failed'}</p>
              {failureCount >= maxFailures ? (
                <Button
                  onClick={forceNewAnalysis}
                  size="sm"
                  variant="outline"
                  className="text-brand-dark-blue border-brand-dark-blue hover:bg-brand-dark-blue/5"
                >
                  <Trans>Force New Analysis</Trans>
                </Button>
              ) : (
                <Button
                  onClick={() => analyzeAudio()}
                  size="sm"
                  variant="outline"
                  className="text-brand-dark-blue border-brand-dark-blue hover:bg-brand-dark-blue/5"
                >
                  <Trans>Try Again</Trans>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analysis Results - Split into separate cards */}
      {audioAnalysis && (
        <>
          {getLogger().info('AudioSetting rendering audioAnalysis results', {
            metadata: { hasResults: !!audioAnalysis }
          })}
          
          {/* Pitch Analysis Card */}
          {(audioAnalysis.pitch_mean_hz || audioAnalysis.pitchMean) && (
            <Card className="border-2 border-brand-dark-blue/30">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  <Trans>Pitch</Trans>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  <Trans>Your voice pitch: {(audioAnalysis.pitch_mean_hz || audioAnalysis.pitchMean || 0).toFixed(1)} Hz</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <VoiceCharacteristicBellCurve
                  type="pitch"
                  value={audioAnalysis.pitch_mean_hz || audioAnalysis.pitchMean || 0}
                  showTips={true}
                />
              </CardContent>
            </Card>
          )}
          
          {/* Speaking Rate Analysis Card */}
          {(audioAnalysis.speaking_rate_wpm || audioAnalysis.speakingRate) && (
            <Card className="border-2 border-brand-dark-blue/30">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  <Trans>Speaking Rate</Trans>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  <Trans>Your speaking rate: {(audioAnalysis.speaking_rate_wpm || audioAnalysis.speakingRate || 0).toFixed(1)} words per minute</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <VoiceCharacteristicBellCurve
                  type="speakingRate"
                  value={audioAnalysis.speaking_rate_wpm || audioAnalysis.speakingRate || 0}
                  showTips={true}
                />
              </CardContent>
            </Card>
          )}
          
          {/* Volume Analysis Card */}
          {(audioAnalysis.energy_level || audioAnalysis.energyLevel) && (
            <Card className="border-2 border-brand-dark-blue/30">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  <Trans>Volume</Trans>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  <Trans>Your voice energy level: {(audioAnalysis.energy_level || audioAnalysis.energyLevel || 'medium').charAt(0).toUpperCase() + (audioAnalysis.energy_level || audioAnalysis.energyLevel || 'medium').slice(1)}</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <VoiceCharacteristicBellCurve
                  type="volume"
                  value={(() => {
                    const energy = audioAnalysis.energy_level || audioAnalysis.energyLevel || '';
                    // Convert energy level to approximate dB value
                    switch (energy.toLowerCase()) {
                      case 'low': return 50;
                      case 'medium': return 65;
                      case 'high': return 80;
                      default: return 65;
                    }
                  })()}
                  showTips={true}
                />
              </CardContent>
            </Card>
          )}
          
          {/* Fallback message if no bell curve data available */}
          {!((audioAnalysis.pitch_mean_hz || audioAnalysis.pitchMean) || 
             (audioAnalysis.speaking_rate_wpm || audioAnalysis.speakingRate) || 
             (audioAnalysis.energy_level || audioAnalysis.energyLevel)) && (
            <Card className="border-2 border-brand-dark-blue/30">
              <CardContent>
                <div className="text-center py-8">
                  <div className="text-brand-dark-blue/50 mb-4">
                    <Volume2 className="h-12 w-12 mx-auto" />
                  </div>
                  <p className="text-sm text-brand-dark-blue/70">
                    <Trans>Bell curve comparison will be available once we have more detailed voice analysis data.</Trans>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Manual Analysis Button */}
      {!audioAnalysis && !isLoading && !error && (
        <Card className="border-2 border-brand-dark-blue/30">
          <CardContent>
            <div className="text-center py-8">
              <Button
                onClick={() => analyzeAudio()}
                className="bg-brand-dark-blue text-brand-white hover:bg-brand-dark-blue/90 font-semibold"
              >
                <Volume2 className="h-4 w-4 mr-2" />
                <Trans>Analyze Audio</Trans>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
